import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { axios } from "@/client/libs/axios";
import { socket } from "@/client/libs/socket";
import { queryClient } from "@/client/libs/query-client";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  CAMPAGNES DE COMMUNICATION (console admin) — services client
 * ─────────────────────────────────────────────────────────────────────────────
 *  Contrat : docs/markdown/campagnes-api.md
 *  Temps réel : socket `campaign:update` → suivi d'envoi live.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export type CampaignChannel = "inApp" | "push" | "email";
export type CampaignPriority = "low" | "normal" | "high";
export type CampaignStatus =
  | "draft"
  | "scheduled"
  | "sending"
  | "completed"
  | "cancelled"
  | "failed";

/** Blocs e-mail riches (contrat identique côté serveur). */
export type EmailBlocks = {
  heroImageUrl?: string;
  intro?: string;
  sections?: { heading?: string; body?: string }[];
  ctaLabel?: string;
  ctaUrl?: string;
  secondaryCtaLabel?: string;
  secondaryCtaUrl?: string;
  note?: string;
};

export type Campaign = {
  id: string;
  title: string;
  body?: string | null;
  link?: string | null;
  segment?: Record<string, unknown> | null;
  channels: CampaignChannel[];
  priority: CampaignPriority;
  status: CampaignStatus;
  scheduledAt?: string | null;
  sentAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
  totalAudience?: number | null;
  createdCount?: number | null;
  readCount?: number | null;
  processedCount?: number | null;
  lastError?: string | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  // ── E-mail riche & sources d'audience ──────────────────────────────────────
  emailSubject?: string | null;
  emailPreheader?: string | null;
  emailBlocks?: EmailBlocks | null;
  includePlatformUsers?: boolean;
  provisionBatchId?: string | null;
  includeContacts?: boolean;
};

export type CampaignContact = {
  id: string;
  email: string;
  name?: string | null;
  source: string;
  isBlacklisted: boolean;
  blacklistedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ContactImportResult = {
  totalRows: number;
  imported: number;
  duplicatesInFile: number;
  alreadyExisting: number;
  renamed?: number;
  invalid: number;
  errors: { line: number; email: string | null; reason: string }[];
};

export type ExternalRecipient = {
  id: string;
  contactId: string;
  email: string;
  name?: string | null;
  isBlacklisted?: boolean;
  emailSent: boolean;
  error?: string | null;
  sentAt?: string | null;
};

export type CampaignRecipient = {
  id: string;
  userId: string;
  name?: string;
  email?: string;
  inAppSent: boolean;
  notificationId?: string | null;
  error?: string | null;
  sentAt?: string | null;
};

export type SegmentCriteria = {
  key: string;
  label: string;
  description?: string;
  /**
   * Élargi en `string` : le serveur émet aussi "number[]" / "string[]"
   * (ex. roles, excludeUserIds) au-delà des types scalaires d'origine.
   */
  type: string;
  options?: { value: string | number; label: string }[];
};

export type Segment = Record<string, unknown>;

export const useCampaigns = (params: { status?: string; search?: string } = {}) => {
  const query = useQuery({
    queryKey: CAMPAIGN_KEYS.list(params.status, params.search),
    queryFn: () => fetchCampaigns(params),
  });

  const queryClientRef = useQueryClient();
  useEffect(() => {
    const invalidate = () => queryClientRef.invalidateQueries({ queryKey: CAMPAIGN_KEYS.all });
    socket.on("campaign:update", invalidate);
    return () => {
      socket.off("campaign:update", invalidate);
    };
  }, [queryClientRef]);

  return { campaigns: query.data?.items ?? [], loading: query.isPending, error: query.error };
};

export const useCampaign = (id: string) => {
  const query = useQuery({
    queryKey: CAMPAIGN_KEYS.detail(id),
    queryFn: () => fetchCampaign(id),
    enabled: Boolean(id),
    refetchInterval: (q) => (q.state.data?.status === "sending" ? 3000 : false),
  });

  const queryClientRef = useQueryClient();
  useEffect(() => {
    const invalidate = () =>
      queryClientRef.invalidateQueries({ queryKey: CAMPAIGN_KEYS.detail(id) });
    socket.on("campaign:update", invalidate);
    return () => {
      socket.off("campaign:update", invalidate);
    };
  }, [queryClientRef, id]);

  return { campaign: query.data, loading: query.isPending };
};

export const useCampaignRecipients = (id: string) => {
  const query = useQuery({
    queryKey: CAMPAIGN_KEYS.recipients(id),
    queryFn: () => fetchRecipients(id),
    enabled: Boolean(id),
  });
  return { recipients: query.data?.items ?? [], loading: query.isPending };
};

export const useSegmentCriteria = () => {
  const query = useQuery({
    queryKey: CAMPAIGN_KEYS.criteria,
    queryFn: async () => {
      const response = await axios.get<{ criteria: SegmentCriteria[] }>(
        "/campaigns/segments/criteria",
      );
      return response.data.criteria;
    },
    staleTime: 10 * 60 * 1000,
  });
  return { criteria: query.data ?? [], loading: query.isPending };
};

/** Compteur d'audience live (debounce géré par l'appelant). */
export const useSegmentCount = (segment: Segment | null) => {
  return useQuery({
    queryKey: ["campaigns", "count", segment],
    queryFn: async () => {
      const response = await axios.post<{ total: number }>("/campaigns/segments/count", {
        segment,
      });
      return response.data.total;
    },
    enabled: Boolean(segment) && Object.keys(segment ?? {}).length > 0,
    staleTime: 30 * 1000,
  });
};

export const CAMPAIGN_KEYS = {
  all: ["campaigns"] as const,
  list: (status?: string, search?: string) => ["campaigns", "list", status, search] as const,
  detail: (id: string) => ["campaigns", "detail", id] as const,
  recipients: (id: string) => ["campaigns", "recipients", id] as const,
  external: (id: string) => ["campaigns", "external", id] as const,
  criteria: ["campaigns", "criteria"] as const,
  contacts: ["campaigns", "contacts"] as const,
  contactStats: ["campaigns", "contact-stats"] as const,
};

const fetchCampaigns = async (params: { status?: string; search?: string }) => {
  const response = await axios.get<{ items: Campaign[]; nextCursor: string | null }>("/campaigns", {
    params,
  });
  return response.data;
};

const fetchCampaign = async (id: string) => {
  const response = await axios.get<Campaign>(`/campaigns/${id}`);
  return response.data;
};

const fetchRecipients = async (id: string) => {
  const response = await axios.get<{ items: CampaignRecipient[]; nextCursor: string | null }>(
    `/campaigns/${id}/recipients`,
    { params: { limit: 100 } },
  );
  return response.data;
};

export const useSegmentPreview = () => {
  return useMutation({
    mutationFn: async (segment: Segment) => {
      const response = await axios.post<{
        total: number;
        sample: { id: string; name: string; email: string; role: number; createdAt: string }[];
      }>("/campaigns/segments/preview", { segment });
      return response.data;
    },
  });
};

export const useCreateCampaign = () => {
  return useMutation({
    mutationFn: async (payload: {
      title: string;
      body?: string;
      link?: string;
      segment?: Segment;
      channels: CampaignChannel[];
      priority: CampaignPriority;
      scheduledAt?: string;
      // ── E-mail riche ─────────────────────────────────────────────────────────
      emailSubject?: string;
      emailPreheader?: string;
      emailBlocks?: EmailBlocks;
      // ── Sources d'audience ───────────────────────────────────────────────────
      includePlatformUsers?: boolean;
      provisionBatchId?: string;
      includeContacts?: boolean;
    }) => {
      const response = await axios.post<Campaign>("/campaigns", payload);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CAMPAIGN_KEYS.all }),
  });
};

export const useSendCampaign = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.post<Campaign>(`/campaigns/${id}/send`);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CAMPAIGN_KEYS.all }),
  });
};

export const useCancelCampaign = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.post<Campaign>(`/campaigns/${id}/cancel`);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CAMPAIGN_KEYS.all }),
  });
};

export const useDeleteCampaign = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete<{ ok: boolean }>(`/campaigns/${id}`);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CAMPAIGN_KEYS.all }),
  });
};

export const useSendTestCampaign = () => {
  return useMutation({
    mutationFn: async ({ id, email }: { id: string; email: string }) => {
      const response = await axios.post<{
        delivered: boolean;
        reason?: string;
        notificationId?: string;
      }>(`/campaigns/${id}/test`, { email });
      return response.data;
    },
  });
};

export const useUpdateCampaign = () => {
  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: {
      id: string;
      title?: string;
      body?: string;
      link?: string;
      segment?: Segment;
      channels?: CampaignChannel[];
      priority?: CampaignPriority;
      scheduledAt?: string | null;
      emailSubject?: string | null;
      emailPreheader?: string | null;
      emailBlocks?: EmailBlocks | null;
      includePlatformUsers?: boolean;
      provisionBatchId?: string | null;
      includeContacts?: boolean;
    }) => {
      const response = await axios.patch<Campaign>(`/campaigns/${id}`, payload);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: CAMPAIGN_KEYS.all });
      queryClient.invalidateQueries({ queryKey: CAMPAIGN_KEYS.detail(variables.id) });
    },
  });
};

// ═══════════════════════════════════════════════════════════════════════════
//  CONTACTS EXTERNES (adresses sans compte) & APERÇU E-MAIL
// ═══════════════════════════════════════════════════════════════════════════

export const useImportContacts = () => {
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post<ContactImportResult>(
        "/campaigns/contacts/import",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CAMPAIGN_KEYS.contacts });
      queryClient.invalidateQueries({ queryKey: CAMPAIGN_KEYS.contactStats });
    },
  });
};

export const useContacts = (
  params: { search?: string; blacklisted?: "all" | "only" | "exclude" } = {},
) => {
  const query = useQuery({
    queryKey: [...CAMPAIGN_KEYS.contacts, params],
    queryFn: async () => {
      const response = await axios.get<{
        items: CampaignContact[];
        total: number;
        nextCursor: string | null;
      }>("/campaigns/contacts", { params: { ...params, limit: 100 } });
      return response.data;
    },
  });
  return {
    contacts: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    loading: query.isPending,
  };
};

export const useContactStats = () => {
  const query = useQuery({
    queryKey: CAMPAIGN_KEYS.contactStats,
    queryFn: async () => {
      const response = await axios.get<{ total: number; active: number; blacklisted: number }>(
        "/campaigns/contacts/stats",
      );
      return response.data;
    },
  });
  return { stats: query.data, loading: query.isPending };
};

export const useBlacklistContact = () => {
  return useMutation({
    mutationFn: async ({ email, blacklisted }: { email: string; blacklisted: boolean }) => {
      const response = await axios.post<CampaignContact>("/campaigns/contacts/blacklist", {
        email,
        blacklisted,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CAMPAIGN_KEYS.contacts });
      queryClient.invalidateQueries({ queryKey: CAMPAIGN_KEYS.contactStats });
    },
  });
};

export const useDeleteContact = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`/campaigns/contacts/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CAMPAIGN_KEYS.contacts });
      queryClient.invalidateQueries({ queryKey: CAMPAIGN_KEYS.contactStats });
    },
  });
};

export const useContactDeliveries = (id: string) => {
  const query = useQuery({
    queryKey: CAMPAIGN_KEYS.external(id),
    queryFn: async () => {
      const response = await axios.get<{ items: ExternalRecipient[]; nextCursor: string | null }>(
        `/campaigns/${id}/external-recipients`,
        { params: { limit: 100 } },
      );
      return response.data;
    },
    enabled: Boolean(id),
    refetchInterval: (q) =>
      q.state.data && q.state.data.items.some((r) => !r.sentAt) ? 4000 : false,
  });
  return { deliveries: query.data?.items ?? [], loading: query.isPending };
};

export type EmailPreview = { subject: string; html: string; text: string };

export const usePreviewEmail = () => {
  return useMutation({
    mutationFn: async (payload: {
      title: string;
      body?: string;
      link?: string;
      emailSubject?: string;
      emailBlocks?: EmailBlocks;
      sampleName?: string;
      sampleEmail?: string;
    }) => {
      const response = await axios.post<EmailPreview>("/campaigns/preview-email", payload);
      return response.data;
    },
  });
};

/** Désabonnement public (page /unsubscribe) — aucune auth requise. */
export const unsubscribeExternal = async (contactId: string, token: string) => {
  const response = await axios.post<{ ok: boolean }>("/campaigns/public/unsubscribe", {
    contactId,
    token,
  });
  return response.data;
};
